import React from 'react';
import styled from 'styled-components';
import { Button, Icon, Modal, Note } from '@repo/design-system';
import { ink, space, typeRole } from '@repo/design-tokens';
import type { DroppedDraftSummary } from '@repo/frontend-types';

import type { DraftChoiceError } from './useDraftChoice';
import DraftChoiceErrorBanner from './DraftChoiceErrorBanner';
import { whenText } from './draftChoiceText';
import { ChangePills, type ChangesState } from './VersionsModal';

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};

  p {
    margin: 0;
  }

  .changes-line {
    font-weight: ${typeRole.caption1.fontWeight};
    font-size: ${typeRole.caption1.fontSize};
    line-height: ${typeRole.caption1.lineHeight};
    color: ${ink[3]};
  }
`;

export interface DiscardDraftModalProps {
  open: boolean;
  /** Production's launch number, for *a copy of production (v{N})*; null to say production alone. */
  productionVersion: string | null;
  /** The same changes Versions showed (R4), or null where it shows none. */
  changes: ChangesState | null;
  dropped: DroppedDraftSummary | null;
  droppedReadFailed: boolean;
  /** The dropped-draft read is outstanding: Discard waits, so its "replaces" line is never skipped. */
  droppedChecking: boolean;
  busy: boolean;
  error: DraftChoiceError | null;
  onDiscard: () => void;
  onCancel: () => void;
  onReload: () => void;
}

/**
 * *Discard your draft?* (`twin-details-page-versions`, R8.1; frame 08 note 3): the draft becomes a
 * copy of production, and its changes — listed by step, as Versions listed them — go to the dropped
 * draft, restorable until the next launch. Coral as the design draws it ("reserved red for the
 * action"), though `Modal` keeps coral for irreversible deletes: owed Rob's ratification.
 *
 * Dismissible until the swap runs. Its two dropped-draft lines are `LoadLaunchModal`'s, so the two
 * confirmations say the same thing about the one slot.
 */
const DiscardDraftModal: React.FC<DiscardDraftModalProps> = ({
  open,
  productionVersion,
  changes,
  dropped,
  droppedReadFailed,
  droppedChecking,
  busy,
  error,
  onDiscard,
  onCancel,
  onReload,
}) => (
  <Modal
    open={open}
    onClose={onCancel}
    dismissible={!busy}
    title="Discard your draft?"
    tone="coral"
    width="md"
    icon={<Icon name="rotate-left" variant="solid" size="xs" />}
    actions={
      <>
        <Button type="button" variant="frosted" onClick={onCancel} disabled={busy}>
          Keep Draft
        </Button>
        <Button
          type="button"
          variant="destructive"
          onClick={onDiscard}
          loading={busy}
          disabled={droppedChecking}
        >
          Discard Draft
        </Button>
      </>
    }
  >
    <Body>
      <p>
        Your draft becomes a copy of production{productionVersion ? ` (${productionVersion})` : ''}.
      </p>
      {changes?.state === 'ready' && changes.pills.length > 0 && (
        <ChangePills pills={changes.pills} />
      )}
      {changes?.state === 'loading' && (
        <p className="changes-line" role="status">
          Working out what your draft changes…
        </p>
      )}
      {changes?.state === 'failed' && (
        <p className="changes-line">We couldn&apos;t list what your draft changes.</p>
      )}
      <p>
        It&apos;s kept as the dropped draft, and you can restore it from Versions until the next
        launch.
      </p>
      {droppedChecking && (
        <p className="changes-line" role="status">
          Checking for a dropped draft…
        </p>
      )}
      {dropped && <p>This replaces the draft you dropped {whenText(dropped.droppedAt)}.</p>}
      {droppedReadFailed && (
        <Note tone="butter">
          We couldn&apos;t check for a dropped draft, so this may replace one.
        </Note>
      )}
      <p>Opening hours, name, address, timezone and the asset register stay as they are.</p>
      {error && <DraftChoiceErrorBanner error={error} onReload={onReload} />}
    </Body>
  </Modal>
);

export default DiscardDraftModal;
